"use client";

import { ReactNode, useState } from "react";
import { SquareChevronLeft, SquareChevronRight } from "lucide-react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "../typography";
import { useSidebarContext } from "./sidebar-context";
import { SidebarMobileMenuClose } from "./sidebar";

type Props = {
  title: string;
  /** Logo/wordmark rendered when expanded (desktop) or in the mobile
   * slide-over menu. */
  logo: ReactNode;
  /** Compact mark rendered when the desktop rail is collapsed, and in the
   * mobile fixed bar. Defaults to `logo`. */
  collapsedLogo?: ReactNode;
};

/**
 * Logo/title header for a `Sidebar` — the same three logo variants
 * (expanded, collapsed rail, mobile bar) the old `SidebarShell` took as
 * props, now composed as `<SidebarHeader logo={...} collapsedLogo={...} />`
 * inside `<Sidebar>`. Adapts its own markup per `useSidebarContext().slot`
 * rather than the parent needing to know about mobile vs desktop.
 */
export const SidebarHeader = ({ title, logo, collapsedLogo }: Props) => {
  const { slot, isExpandable, isExpanded, toggleSidebar, showContent } = useSidebarContext();
  const [isHovering, setIsHovering] = useState(false);
  const resolvedCollapsedLogo = collapsedLogo ?? logo;

  if (slot === "mobile-bar" || slot === "mobile-menu") {
    return (
      <div
        className={cn(
          "flex min-w-0 items-center",
          slot === "mobile-menu" ? "shrink-0 p-2" : "flex-1",
        )}
      >
        <div className="h-3 w-3 shrink-0">{resolvedCollapsedLogo}</div>
        <div className="ml-1 min-w-0 flex-1 overflow-hidden">
          <Typography size="sm" className="truncate whitespace-nowrap" weight="semibold">
            {title}
          </Typography>
        </div>
        {slot === "mobile-menu" && <SidebarMobileMenuClose />}
      </div>
    );
  }

  // Desktop
  const collapsedExpandable = !isExpanded && isExpandable;

  return (
    <div
      className={cn(
        "flex items-center",
        isExpanded ? "justify-between pr-1 pl-2" : "justify-center pt-2",
        collapsedExpandable && "cursor-e-resize",
      )}
      role={collapsedExpandable ? "button" : undefined}
      tabIndex={collapsedExpandable ? 0 : undefined}
      aria-label={collapsedExpandable ? "Expand sidebar" : undefined}
      onClick={() => collapsedExpandable && toggleSidebar()}
      onKeyDown={(e) => {
        if (collapsedExpandable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          toggleSidebar();
        }
      }}
      onMouseEnter={() => collapsedExpandable && setIsHovering(true)}
      onMouseLeave={() => collapsedExpandable && setIsHovering(false)}
    >
      <div className={cn("flex min-w-0 items-center", isExpanded ? "flex-1" : "")}>
        <div className="relative shrink-0">
          {isExpanded ? (
            logo
          ) : (
            <>
              <div className={cn("transition-opacity duration-200", isHovering && "opacity-0")}>
                {resolvedCollapsedLogo}
              </div>
              <SquareChevronRight
                className={cn(
                  "absolute top-1/2 left-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 text-black transition-opacity duration-200",
                  isHovering ? "opacity-100" : "opacity-0",
                )}
              />
            </>
          )}
        </div>
      </div>

      {isExpanded && isExpandable && (
        <button
          onClick={toggleSidebar}
          aria-label="Collapse sidebar"
          className={cn(
            "group -mr-2 flex h-8 w-8 items-center justify-center rounded-md transition-opacity duration-200 ease-in-out",
            showContent ? "opacity-100" : "opacity-0",
          )}
        >
          <SquareChevronLeft className="h-2.5 w-2.5 cursor-pointer text-neutral-900 transition-colors group-hover:text-black" />
        </button>
      )}
    </div>
  );
};
